/**
 * OpenAI API Key (non-admin) storage.
 */
import { STORAGE_VERSION, StoredApiKey, sanitizeApiKey, encode, decode } from './internal';

const OPENAI_API_KEY = 'openai_api_key';

/**
 * Save OpenAI API Key
 */
export const saveOpenAIApiKey = (apiKey: string): boolean => {
  try {
    if (!apiKey || typeof apiKey !== 'string') {
      throw new Error('Invalid API key');
    }
    const normalizedApiKey = sanitizeApiKey(apiKey);
    if (!normalizedApiKey) {
      throw new Error('Invalid API key');
    }

    const data: StoredApiKey = {
      value: encode(normalizedApiKey),
      createdAt: Date.now(),
      version: STORAGE_VERSION
    };

    localStorage.setItem(OPENAI_API_KEY, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Failed to save OpenAI API key:', error);
    return false;
  }
};

/**
 * Get OpenAI API Key
 */
export const getOpenAIApiKey = (): string | null => {
  try {
    // First, check environment variable
    if (import.meta.env.VITE_OPENAI_API_KEY) {
      return import.meta.env.VITE_OPENAI_API_KEY;
    }

    // Fall back to local storage
    const stored = localStorage.getItem(OPENAI_API_KEY);
    if (!stored) return null;

    const data: StoredApiKey = JSON.parse(stored);

    if (data.version !== STORAGE_VERSION) {
      localStorage.removeItem(OPENAI_API_KEY);
      return null;
    }

    const decoded = decode(data.value);
    if (!decoded) return null;
    return sanitizeApiKey(decoded) || null;
  } catch (error) {
    console.error('Failed to get OpenAI API key:', error);
    localStorage.removeItem(OPENAI_API_KEY);
    return null;
  }
};

/**
 * Remove OpenAI API Key
 */
export const removeOpenAIApiKey = (): boolean => {
  try {
    localStorage.removeItem(OPENAI_API_KEY);
    return true;
  } catch (error) {
    console.error('Failed to remove OpenAI API key:', error);
    return false;
  }
};

/**
 * Check if OpenAI API Key exists
 */
export const hasOpenAIApiKey = (): boolean => {
  if (import.meta.env.VITE_OPENAI_API_KEY) {
    return true;
  }
  return getOpenAIApiKey() !== null;
};
